import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../theme/ThemeContext'
import { api, ApiError } from '../../lib/api'
import { type SessionUser } from '@jr2os/shared'
import LanguageSwitcher from '../../components/LanguageSwitcher'

export default function Profile() {
  const auth = useAuth()
  const { theme } = useTheme()
  const { t } = useTranslation(['settings', 'common'])
  const user = auth.status === 'authenticated' ? auth.user : null
  const [fullName, setFullName] = useState(user?.fullName ?? '')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const isLight = theme === 'light'

  useEffect(() => {
    if (user) setFullName(user.fullName)
  }, [user])

  if (!user) return null

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      await api.patch<{ user: SessionUser }>('/auth/me', { fullName })
      await auth.refresh()
      setMessage({ kind: 'ok', text: t('settings:profile.saved') })
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : t('common:errors.network')
      setMessage({ kind: 'error', text })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">
          {t('settings:profile.title')}
        </h2>
        <p className="t-muted text-sm mt-1">{t('settings:profile.subtitle')}</p>
      </header>

      <div className="surface p-6">
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('common:fields.email')}</label>
            <input
              value={user.email}
              disabled
              className="w-full px-3.5 py-2 rounded-lg bg-[var(--bg-input-soft)] border border-[var(--border)]/70 t-muted cursor-not-allowed"
            />
            <p className="text-xs t-faint mt-1">{t('settings:profile.emailHelp')}</p>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('settings:profile.role')}</label>
            <input
              value={t('enums:Role.' + user.role)}
              disabled
              className="w-full px-3.5 py-2 rounded-lg bg-[var(--bg-input-soft)] border border-[var(--border)]/70 t-muted cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">
              {t('settings:profile.fullName')}
            </label>
            <input
              required
              minLength={2}
              maxLength={200}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3.5 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
            />
          </div>

          {message && (
            <div
              className={[
                'text-sm rounded-lg px-3 py-2 border',
                message.kind === 'ok'
                  ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40'
                  : 'text-rose-300 bg-rose-500/10 border-rose-500/30',
              ].join(' ')}
            >
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={saving || fullName === user.fullName}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 transition-all flex items-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {t('common:actions.save')}
          </button>
        </form>
      </div>

      <div className="surface p-6">
        <h3 className="text-lg font-semibold t-primary mb-2">
          {t('settings:profile.languageTitle')}
        </h3>
        <p className="t-muted text-sm mb-4">{t('settings:profile.languageSubtitle')}</p>
        <LanguageSwitcher isLight={isLight} variant="inline" />
      </div>
    </div>
  )
}
